import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { hostUI } from '../../../../configuraciones';

import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScienceIcon from '@mui/icons-material/Science';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import NoteAltIcon from '@mui/icons-material/NoteAlt';

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const num = Number(bytes);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch (_) {
    return String(dateStr);
  }
};

const TIPOS_DOCUMENTO = [
  {
    id: 'analisis_fisicoquimico',
    nombre: 'Análisis Fisicoquímico',
    desc: 'Informe o análisis de laboratorio emitido para el lote (puedes subir varios)',
    icon: ScienceIcon,
    color: '#0284C7',
    bg: '#F0F9FF',
    border: '#BAE6FD'
  },
  {
    id: 'certificado_granel',
    nombre: 'Certificado de Producto a Granel',
    desc: 'Certificado oficial NOM-070 para mezcal a granel (puedes subir varios)',
    icon: PictureAsPdfIcon,
    color: '#16A34A',
    bg: '#F0FDF4',
    border: '#BBF7D0'
  },
  {
    id: 'informe_servicio',
    nombre: 'Informe de Servicio',
    desc: 'Informe o acta de verificación en campo (puedes subir varios)',
    icon: AssignmentIcon,
    color: '#7C3AED',
    bg: '#FAF5FF',
    border: '#E9D5FF'
  },
  {
    id: 'otro',
    nombre: 'Otro Documento',
    desc: 'Constancias, hojas de traslado o anexos complementarios (puedes subir varios)',
    icon: FolderSharedIcon,
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FDE68A'
  }
];

const ModalDocumentosGranel = ({
  isOpen,
  onClose,
  granel,
  onDocumentUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [activeTab, setActiveTab] = useState('listado'); // 'listado' | 'nuevo'
  
  // Formulario nuevo documento
  const [tipoDocumento, setTipoDocumento] = useState('analisis_fisicoquimico');
  const [nombreDocumento, setNombreDocumento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);

  const fileInputRef = useRef(null);

  const idGranel = granel?.rawId || granel?.id_bitacora_granel || granel?.id || granel?.dbId;

  // Consultar lista de documentos del granel
  const fetchDocumentos = async () => {
    if (!idGranel) return;
    setLoading(true);
    try {
      const res = await axios.get(`${hostUI}bitacora-produccion/granel/${idGranel}/documentos`);
      if (res.data?.data) {
        setDocumentos(res.data.data);
      } else {
        setDocumentos([]);
      }
    } catch (error) {
      console.error("Error al obtener documentos del granel:", error);
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && idGranel) {
      fetchDocumentos();
      setActiveTab('listado');
      resetForm();
    }
  }, [isOpen, idGranel]);

  if (!isOpen || !granel) return null;

  const noLote = granel.no_lote || granel.noLote || `ID #${idGranel}`;
  const noTanque = granel.no_tanque || granel.noTanque || "S/N";
  const litros = granel.litros !== undefined ? Number(granel.litros).toLocaleString('es-MX') : '0';
  const claveFqActual = granel.clave_fq || granel.claveFq || "Sin registrar";

  const resetForm = () => {
    setTipoDocumento('analisis_fisicoquimico');
    setNombreDocumento('');
    setObservaciones('');
    setArchivosSeleccionados([]);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleSubirDocumento = async (e) => {
    e.preventDefault();
    if (!archivosSeleccionados || archivosSeleccionados.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo requerido',
        text: 'Por favor selecciona al menos un archivo PDF o imagen para subir.'
      });
      return;
    }

    if (tipoDocumento === 'otro' && !nombreDocumento.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Por favor ingresa el título o nombre del documento.'
      });
      return;
    }

    const tipoObj = TIPOS_DOCUMENTO.find(t => t.id === tipoDocumento);
    const nombreBase = tipoDocumento === 'otro'
      ? nombreDocumento.trim()
      : (tipoObj?.nombre || tipoDocumento);
    const fechaAutomatica = new Date().toISOString().split('T')[0];

    setSubiendo(true);
    try {
      for (let i = 0; i < archivosSeleccionados.length; i++) {
        const archivo = archivosSeleccionados[i];
        const formData = new FormData();
        formData.append('archivo', archivo);
        formData.append('tipo_documento', tipoDocumento);
        const tituloFinal = archivosSeleccionados.length > 1
          ? `${nombreBase} (${i + 1})`
          : nombreBase;
        formData.append('nombre_documento', tituloFinal);
        formData.append('fecha_documento', fechaAutomatica);
        if (observaciones.trim()) formData.append('observaciones', observaciones.trim());

        await axios.post(
          `${hostUI}bitacora-produccion/granel/${idGranel}/documentos`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      Swal.fire({
        icon: 'success',
        title: archivosSeleccionados.length > 1 ? 'Documentos adjuntados' : 'Documento adjuntado',
        text: archivosSeleccionados.length > 1
          ? `Se han adjuntado ${archivosSeleccionados.length} documentos exitosamente al lote a granel.`
          : 'El documento se ha adjuntado exitosamente al lote a granel.',
        timer: 2000,
        showConfirmButton: false
      });

      resetForm();
      setActiveTab('listado');
      await fetchDocumentos();

      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error("Error al subir documento:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error al subir',
        text: error.response?.data?.message || error.message || 'No se pudo adjuntar el documento.'
      });
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminarDocumento = async (doc) => {
    const result = await Swal.fire({
      title: '¿Eliminar documento?',
      text: `Se eliminará permanentemente "${doc.nombre_documento || doc.nombre_original}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${hostUI}bitacora-produccion/granel/documentos/${doc.id_granel_documento}`);
      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: 'El documento ha sido eliminado.',
        timer: 1600,
        showConfirmButton: false
      });
      await fetchDocumentos();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error("Error al eliminar documento:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudo eliminar el documento.'
      });
    }
  };

  const handleVerDocumento = (doc) => {
    const fileUrl = `${hostUI}bitacora-produccion/granel/documentos/${doc.id_granel_documento}/ver`;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  // Contadores por tipo
  const countByType = (typeId) => documentos.filter(d => d.tipo_documento === typeId).length;

  return createPortal(
    <div className="modal-backdrop-custom animate-fadeIn" onClick={onClose} style={{ zIndex: 1050 }}>
      <div
        className="detail-modal-card animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "860px", width: "95%", borderRadius: "14px", overflow: "hidden" }}
      >
        {/* HEADER */}
        <div
          className="detail-modal-header"
          style={{
            background: "linear-gradient(135deg, #065F46 0%, #059669 100%)",
            color: "white",
            padding: "16px 22px"
          }}
        >
          <div className="detail-modal-title-box">
            <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
              <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                DOCUMENTACIÓN DE LOTE
              </span>
              <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                SECCIÓN V: GRANEL
              </span>
            </div>
            <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px", margin: 0 }}>
              📄 Expediente de Documentos del Lote: {noLote}
            </h1>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="detail-modal-close-btn"
            title="Cerrar ventana"
            aria-label="Cerrar modal"
            style={{ color: "white", background: "rgba(255,255,255,0.15)", borderRadius: "50%" }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* SUBHEADER: METADATA DEL LOTE */}
        <div style={{ background: "#F0FDF4", borderBottom: "1px solid #BBF7D0", padding: "12px 22px", display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", color: "#166534" }}>
              <strong>Contenedor / Tanque:</strong> {noTanque}
            </span>
            <span style={{ fontSize: "13px", color: "#166534" }}>
              <strong>Existencia:</strong> {litros} L
            </span>
            <span style={{ fontSize: "13px", color: "#166534" }}>
              <strong>Clave FQ:</strong> {claveFqActual}
            </span>
          </div>
          <button
            type="button"
            onClick={fetchDocumentos}
            disabled={loading}
            className="btn btn-sm btn-outline-success"
            style={{ fontSize: "12px", padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: "4px" }}
            title="Refrescar lista"
          >
            <RefreshIcon style={{ fontSize: "15px" }} /> Actualizar
          </button>
        </div>

        {/* NAVEGACIÓN DE PESTAÑAS */}
        <div style={{ display: "flex", borderBottom: "1px solid #E5E7EB", background: "#F9FAFB", padding: "0 22px" }}>
          <button
            type="button"
            onClick={() => setActiveTab('listado')}
            style={{
              padding: "12px 18px",
              border: "none",
              background: "none",
              borderBottom: activeTab === 'listado' ? "3px solid #059669" : "3px solid transparent",
              color: activeTab === 'listado' ? "#059669" : "#6B7280",
              fontWeight: activeTab === 'listado' ? "700" : "500",
              fontSize: "14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <AssignmentIcon style={{ fontSize: 18 }} />
            Expediente ({documentos.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('nuevo')}
            style={{
              padding: "12px 18px",
              border: "none",
              background: "none",
              borderBottom: activeTab === 'nuevo' ? "3px solid #059669" : "3px solid transparent",
              color: activeTab === 'nuevo' ? "#059669" : "#6B7280",
              fontWeight: activeTab === 'nuevo' ? "700" : "500",
              fontSize: "14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <AddCircleOutlineIcon style={{ fontSize: 18 }} />
            Adjuntar Nuevo Documento
          </button>
        </div>

        {/* BODY */}
        <div style={{ padding: "20px 22px", maxHeight: "65vh", overflowY: "auto" }}>
          {activeTab === 'listado' && (
            <div>
              {/* RESUMEN DE CATEGORÍAS */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginBottom: "18px" }}>
                {TIPOS_DOCUMENTO.map((tipo) => {
                  const count = countByType(tipo.id);
                  const Icon = tipo.icon;
                  return (
                    <div
                      key={tipo.id}
                      style={{
                        background: tipo.bg,
                        border: `1px solid ${tipo.border}`,
                        borderRadius: "8px",
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                      }}
                    >
                      <div style={{ background: "white", padding: "6px", borderRadius: "6px", color: tipo.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon style={{ fontSize: 20 }} />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: "11px", color: "#4B5563", fontWeight: 600, textTransform: "uppercase" }}>{tipo.nombre}</div>
                        <div style={{ fontSize: "13px", fontWeight: 800, color: tipo.color }}>
                          {count === 0 ? "Sin adjuntos" : `${count} archivo${count > 1 ? 's' : ''}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* LISTA DE ARCHIVOS */}
              {loading ? (
                <div style={{ textAlign: "center", padding: "30px", color: "#6B7280" }}>
                  <div className="spinner-border text-success" role="status"></div>
                  <p style={{ marginTop: "10px" }}>Cargando documentos del lote...</p>
                </div>
              ) : documentos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", background: "#F9FAFB", borderRadius: "10px", border: "1px dashed #D1D5DB" }}>
                  <UploadFileIcon style={{ fontSize: 48, color: "#9CA3AF", marginBottom: "8px" }} />
                  <h4 style={{ fontSize: "16px", color: "#374151", margin: "0 0 6px 0" }}>No hay documentos adjuntos aún</h4>
                  <p style={{ fontSize: "13px", color: "#6B7280", margin: "0 0 16px 0" }}>
                    Puedes subir el Análisis Fisicoquímico, el Certificado a Granel o múltiples Informes de Servicio.
                  </p>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => setActiveTab('nuevo')}
                    style={{ background: "#059669", borderColor: "#059669", fontWeight: 600 }}
                  >
                    + Adjuntar Documento Ahora
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {documentos.map((doc) => {
                    const infoTipo = TIPOS_DOCUMENTO.find(t => t.id === doc.tipo_documento) || {
                      nombre: doc.tipo_documento,
                      color: "#4B5563",
                      bg: "#F3F4F6",
                      border: "#E5E7EB",
                      icon: FolderSharedIcon
                    };
                    const TipoIcon = infoTipo.icon;

                    return (
                      <div
                        key={doc.id_granel_documento}
                        style={{
                          border: "1px solid #E5E7EB",
                          borderRadius: "10px",
                          padding: "12px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: "white",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                          <div style={{ background: infoTipo.bg, color: infoTipo.color, border: `1px solid ${infoTipo.border}`, padding: "8px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <TipoIcon style={{ fontSize: 22 }} />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "3px" }}>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 800,
                                  color: infoTipo.color,
                                  background: infoTipo.bg,
                                  border: `1px solid ${infoTipo.border}`,
                                  borderRadius: "4px",
                                  padding: "1px 6px"
                                }}
                              >
                                {infoTipo.nombre}
                              </span>
                              {doc.folio_documento && (
                                <span style={{ fontSize: "11px", fontWeight: 700, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: "4px", padding: "1px 6px" }}>
                                  Folio: {doc.folio_documento}
                                </span>
                              )}
                              <span style={{ fontSize: "11px", color: "#9CA3AF" }}>
                                Subido: {formatDate(doc.fecha_subida)}
                              </span>
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {doc.nombre_documento || doc.nombre_original || doc.nombre_archivo}
                            </div>
                            <div style={{ fontSize: "12px", color: "#6B7280", display: "flex", gap: "10px", marginTop: "2px" }}>
                              <span>{doc.nombre_original}</span>
                              {doc.tamano_bytes && <span>• {formatFileSize(doc.tamano_bytes)}</span>}
                              {doc.observaciones && <span>• <em>{doc.observaciones}</em></span>}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: "8px", marginLeft: "12px" }}>
                          <button
                            type="button"
                            onClick={() => handleVerDocumento(doc)}
                            className="btn btn-sm btn-outline-primary"
                            style={{ display: "flex", alignItems: "center", gap: "4px", fontWeight: 600, padding: "5px 10px" }}
                            title="Ver / Abrir documento en nueva pestaña"
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} /> Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEliminarDocumento(doc)}
                            className="btn btn-sm btn-outline-danger"
                            style={{ padding: "5px 8px" }}
                            title="Eliminar documento"
                          >
                            <DeleteOutlineIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'nuevo' && (
            <form onSubmit={handleSubirDocumento}>
              <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
                <h4 style={{ fontSize: "15px", color: "#111827", fontWeight: 700, margin: "0 0 12px 0" }}>
                  1. Selecciona el Tipo de Documento
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px" }}>
                  {TIPOS_DOCUMENTO.map((tipo) => {
                    const isSelected = tipoDocumento === tipo.id;
                    const Icon = tipo.icon;
                    return (
                      <div
                        key={tipo.id}
                        onClick={() => setTipoDocumento(tipo.id)}
                        style={{
                          border: isSelected ? `2px solid ${tipo.color}` : "1px solid #D1D5DB",
                          background: isSelected ? tipo.bg : "white",
                          borderRadius: "8px",
                          padding: "10px 12px",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px"
                        }}
                      >
                        <div style={{ color: tipo.color, marginTop: "2px" }}>
                          <Icon style={{ fontSize: 20 }} />
                        </div>
                        <div>
                          <div style={{ fontSize: "13px", fontWeight: isSelected ? 800 : 600, color: isSelected ? tipo.color : "#374151" }}>
                            {tipo.nombre}
                          </div>
                          <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px" }}>
                            {tipo.desc}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
                <h4 style={{ fontSize: "15px", color: "#111827", fontWeight: 700, margin: "0 0 12px 0" }}>
                  2. Datos y Archivo
                </h4>

                <div className="row g-3">
                  {tipoDocumento === 'otro' && (
                    <div className="col-md-6">
                      <label className="form-label" style={{ fontSize: "13px", fontWeight: 600 }}>
                        Título o Nombre del Documento <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej. Constancia de traslado o anexo complementario"
                        value={nombreDocumento}
                        onChange={(e) => setNombreDocumento(e.target.value)}
                        required
                      />
                    </div>
                  )}

                  <div className={tipoDocumento === 'otro' ? "col-md-6" : "col-12"}>
                    <label className="form-label" style={{ fontSize: "13px", fontWeight: 600 }}>
                      Archivo(s) (PDF, JPG, PNG) <span className="text-danger">*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      multiple
                      onChange={(e) => setArchivosSeleccionados(Array.from(e.target.files || []))}
                      required
                    />
                    {archivosSeleccionados.length > 0 && (
                      <small className="text-muted d-block mt-1">
                        {archivosSeleccionados.length === 1 ? (
                          <>
                            Archivo seleccionado: <strong>{archivosSeleccionados[0].name}</strong> ({formatFileSize(archivosSeleccionados[0].size)})
                          </>
                        ) : (
                          <>
                            <strong>{archivosSeleccionados.length} archivos seleccionados:</strong>{' '}
                            {archivosSeleccionados.map(f => `${f.name} (${formatFileSize(f.size)})`).join(', ')}
                          </>
                        )}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: "13px", fontWeight: 600 }}>
                      Observaciones / Notas adicionales <span className="text-muted" style={{ fontWeight: 400 }}>(Opcional)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Agrega cualquier observación o nota relevante sobre este documento..."
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setActiveTab('listado')}
                  disabled={subiendo}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={subiendo || archivosSeleccionados.length === 0 || (tipoDocumento === 'otro' && !nombreDocumento.trim())}
                  style={{ background: "#059669", borderColor: "#059669", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}
                >
                  {subiendo ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      Subiendo documento{archivosSeleccionados.length > 1 ? 's' : ''}...
                    </>
                  ) : (
                    <>
                      <UploadFileIcon style={{ fontSize: 18 }} />
                      Adjuntar Documento{archivosSeleccionados.length > 1 ? `s (${archivosSeleccionados.length})` : ''}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ background: "#F9FAFB", borderTop: "1px solid #E5E7EB", padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "12px", color: "#6B7280" }}>
            Total de documentos adjuntos: <strong>{documentos.length}</strong>
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            style={{ fontWeight: 600 }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalDocumentosGranel;
